import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Booking } from '../../../types';
import { Vehicle } from '../../../models/vehicle';
import { useDeleteDialog } from '../..';
import { GridPaginationModel } from '@mui/x-data-grid';
import { apiDelete, apiGet, apiPost, handleApiError, showErrorToast } from '../../../services/apiClient';
import { toast } from '../../../utils/toast';
import { useTranslation } from 'react-i18next';
import Form, { BookingFormData, FormRef } from '../../../pages/bookings/components/form';
import { API_ENDPOINTS, buildApiUrl } from '../../../config/apiEndpoints';
import { StandardDialog } from '../../../components/dialogs';
import { LoadingPlace, LoadingPlaceTypes } from '../../../models/booking';
import { COUNTRY_NAMES } from '../../../models/country';

/** When filtering all-tenant bookings in the UI, the API may ignore search/status params — fetch a batch and filter client-side. */
const MAX_BOOKINGS_FOR_CLIENT_FILTER = 5000;

function bookingSearchBlob(booking: Record<string, any>): string {
  const parts: string[] = [];
  if (booking.id !== undefined && booking.id !== null) parts.push(String(booking.id));
  if (booking.externalOfferId) parts.push(String(booking.externalOfferId));
  const v = booking.vehicle;
  if (v) {
    if (v.truckPlate) parts.push(String(v.truckPlate));
    if (v.trailerPlate) parts.push(String(v.trailerPlate));
    if (v.name) parts.push(String(v.name));
  }
  for (const p of booking.loadingPlaces || []) {
    const a = p?.address;
    if (a?.city) parts.push(String(a.city));
    if (a?.postalCode) parts.push(String(a.postalCode));
    if (a?.country) {
      parts.push(String(a.country));
      const cn = COUNTRY_NAMES[a.country as keyof typeof COUNTRY_NAMES];
      if (cn) parts.push(cn);
    }
    if (p?.name) parts.push(String(p.name));
    if (p?.goodsType) parts.push(String(p.goodsType));
    if (p?.reference) parts.push(String(p.reference));
  }
  return parts.join(' ').toLowerCase();
}

function matchesStandaloneFilters(
  booking: Record<string, any>,
  searchLower: string,
  statusFilter: string
): boolean {
  if (statusFilter && String(booking.status) !== statusFilter) return false;
  if (!searchLower) return true;
  return bookingSearchBlob(booking).includes(searchLower);
}

interface UseBookingsOptions {
  vehicle?: Vehicle | null;
  onBookingCreated?: () => void;
  standalone?: boolean;
}

const useBookings = (options?: UseBookingsOptions) => {
  const vehicle = options?.vehicle;
  const onBookingCreated = options?.onBookingCreated;
  const standalone = !!options?.standalone;
  const { t } = useTranslation();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [query, setQuery] = useState<Record<string, any>>({});
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [formData, setFormData] = useState<BookingFormData>({});
  const [action, setAction] = useState<'create' | 'update' | 'view'>('create');
  const formRef = useRef<FormRef>(null);
  const { DeleteModal, confirmDelete } = useDeleteDialog();
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 15
  });

  const fetchBookings = useCallback(
    async (vehicleId?: number) => {
      try {
        setLoading(true);
        const { page, pageSize } = paginationModel;

        const url = vehicleId
          ? buildApiUrl(API_ENDPOINTS.vehicle.bookings.list(vehicleId))
          : buildApiUrl(API_ENDPOINTS.bookings.list);

        const trimmedSearch =
          typeof query.search === 'string' ? query.search.trim().toLowerCase() : '';
        const statusFilter =
          query.status !== undefined && query.status !== null && query.status !== '' ? String(query.status) : '';

        const isGlobalList = !vehicleId;
        const useClientFilter =
          isGlobalList && (!!trimmedSearch || !!statusFilter);

        const requestQuery = useClientFilter
          ? { offset: 0, limit: MAX_BOOKINGS_FOR_CLIENT_FILTER }
          : { ...query, offset: page * pageSize, limit: pageSize };

        const resp = await apiGet(url, requestQuery);
        const { result, count } = await resp.json();

        const mapped = result.map((booking: any) => ({
          ...booking,
          // Ensure startPlace/endPlace are LoadingPlace objects for template compatibility
          startPlace: booking.loadingPlaces?.find((p: any) => p.loadingType === LoadingPlaceTypes.LOADING) || booking.loadingPlaces?.[0],
          endPlace: booking.loadingPlaces?.find((p: any) => p.loadingType === LoadingPlaceTypes.UNLOADING) || booking.loadingPlaces?.[booking.loadingPlaces?.length - 1],
          // Extract goodsType from the first loading place for display in the edit form
          goodsType: booking.loadingPlaces?.find((p: any) => p.loadingType === LoadingPlaceTypes.LOADING)?.goodsType,
        }));

        if (useClientFilter) {
          const filtered = mapped.filter((b: Record<string, any>) => matchesStandaloneFilters(b, trimmedSearch, statusFilter));
          const start = page * pageSize;
          setBookings(filtered.slice(start, start + pageSize));
          setRowCount(filtered.length);
        } else {
          setBookings(mapped);
          setRowCount(typeof count === 'number' ? count : mapped.length);
        }
      } catch (error) {
        handleApiError(error, t('Failed to fetch bookings'));
      } finally {
        setLoading(false);
      }
    },
    [query, paginationModel, standalone]
  );

  const handleSubmitWithValidation = async () => {
    if (!formRef.current?.validate()) return;
    await handleSubmit();
  };

  const handleSubmit = async () => {
    const places = formData.loadingPlaces || [];

    // For create: derive booking-level times from the first loading / last unloading place date+time
    // For update: use the booking-level startTime/endTime edited directly in the form
    const toEpochMs = (place?: LoadingPlace): number | '' => {
      if (!place?.earliestLoadingDate) return '';
      const time = place.startTime?.substring(0, 5) || '00:00';
      const dt = new Date(`${place.earliestLoadingDate}T${time}:00`);
      return isNaN(dt.getTime()) ? '' : dt.getTime();
    };

    const firstLoading = places.find(p => p.loadingType === LoadingPlaceTypes.LOADING);
    const lastUnloading = [...places].reverse().find(p => p.loadingType === LoadingPlaceTypes.UNLOADING);

    const startTimeMs = action === 'create'
      ? toEpochMs(firstLoading)
      : (formData.startTime ? new Date(formData.startTime as any).getTime() : '');
    const endTimeMs = action === 'create'
      ? toEpochMs(lastUnloading)
      : (formData.endTime ? new Date(formData.endTime as any).getTime() : '');

    const payload = {
      vehicle_id: formData.vehicle?.id,
      loadingPlaces: places.map(p => p.loadingType === LoadingPlaceTypes.LOADING ? { ...p, goodsType: formData.goodsType } : p),
      startTime: startTimeMs,
      start_time: startTimeMs,
      endTime: endTimeMs,
      end_time: endTimeMs,
      external_offer_id: formData.externalOfferId,
      status: formData.status,
      is_current_booking: formData.is_current_booking || false,
      overtime_details: formData.overtimeDetails
        ? {
          breakHours: formData.overtimeDetails[0] / 60,
          driveHours: (formData.overtimeDetails[1] - formData.overtimeDetails[0]) / 60,
          adminHours: (formData.overtimeDetails[2] - formData.overtimeDetails[1]) / 60,
        }
        : undefined,
    };

    try {
      setSubmitting(true);
      const vehicleId = formData.vehicle?.id;
      if (!vehicleId) return;
      let url;
      if (action === 'create') url = buildApiUrl(API_ENDPOINTS.vehicle.bookings.create(vehicleId));
      if (action === 'update' && formData.id) {
        url = buildApiUrl(API_ENDPOINTS.vehicle.bookings.update(vehicleId, formData.id));
      }

      if (!url) {
        toast.error(t('Invalid action'));
        return;
      }

      await apiPost(url, payload);
      toast.success(`${t('Booking')} ${t('created')}!`);
      if (onBookingCreated) onBookingCreated();
      setFormData({});
      setIsOpen(false);
      setHasValidationErrors(false);
      await fetchBookings(vehicle?.id);
    } catch (error) {
      showErrorToast(error, t('Failed to create booking'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    const booking = bookings.find(item => item.id === id);
    if (!booking || !booking.vehicle_id) {
      toast.error(t('Booking not found'));
      return;
    }
    const bookingLabel = booking?.id ? `Booking #${booking.id}` : t('this booking');
    const ok = await confirmDelete(bookingLabel);
    if (!ok) return;
    try {
      const url = buildApiUrl(API_ENDPOINTS.vehicle.bookings.delete(booking?.vehicle_id, id));
      await apiDelete(url);
      toast.success(t('Successfully deleted!'));
      await fetchBookings(vehicle?.id);
    } catch {
      showErrorToast(undefined, t('Delete failed!'));
    }
  };

  const Dialog =
    <StandardDialog
      open={isOpen}
      onClose={() => { setIsOpen(false); setFormData({}); setAction('create'); setHasValidationErrors(false); }}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography component="span" sx={{ fontWeight: 600, color: 'text.dark', fontSize: (theme: any) => theme.typography.fontSizes.heading2 }}>
            {action === 'create' ? t('Create Booking') : t('View Booking')}
          </Typography>
          {formData.vehicle?.truckPlate && (
            <Typography
              component="span"
              sx={{
                fontWeight: 900,
                fontSize: (theme: any) => theme.typography.fontSizes.heading2,
                color: 'text.dark',
                backgroundColor: 'background.lightGrey',
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                border: '2px solid',
                borderColor: 'border.medium',
                letterSpacing: '0.05em',
                fontFamily: 'monospace'
              }}
            >
              {formData.vehicle.truckPlate}
            </Typography>
          )}
        </Box>
      }
      maxWidth="lg"
      width={{ xs: '100%', sm: '95%', md: '900px', lg: '1000px' }}
      height={{ xs: '100vh', sm: '95vh', md: '88vh', lg: '85vh' }}
      primaryAction={action === 'create' ? {
        label: submitting ? t('Creating...') : t('Create'),
        loading: submitting,
        onClick: handleSubmitWithValidation,
        disabled: submitting || hasValidationErrors
      } : {
        label: submitting ? t('Updating...') : t('Update Booking'),
        onClick: handleSubmitWithValidation,
        loading: submitting,
        disabled: submitting || hasValidationErrors
      }}
      secondaryAction={{
        label: t('Cancel'),
        onClick: () => { setIsOpen(false); setFormData({}); setAction('create'); setHasValidationErrors(false); }
      }}
      contentSx={{ paddingX: { xs: 1.5, sm: 2, md: 2 } }}
    >
      <Form
        ref={formRef}
        formData={formData}
        setFormData={(formData) => setFormData(formData)}
        handleSubmit={handleSubmitWithValidation}
        actionText={<></>}
        hideVehicleSelect={!!vehicle}
        onValidationChange={setHasValidationErrors}
      />
    </StandardDialog>;


  return {
    bookings,
    query,
    isOpen,
    loading,
    formData,
    action,
    DeleteModal,
    rowCount,
    paginationModel,
    Dialog,
    submitting,
    fetchBookings,
    setQuery,
    setIsOpen,
    setFormData,
    setAction,
    setHasValidationErrors,
    handleDelete,
    setPaginationModel,
  };
};

export default useBookings;
